import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { CartContext } from './contexts/CartContext';
import { WishlistContext } from './contexts/WishlistContext';
import { convertCentsToDollar, getCategory } from '../utils'
import config from '../aws-exports'
import { Notification } from 'element-react'
import ResponsiveImage from './ResponsiveImage';
import { Link } from 'react-router-dom';

const {
    aws_user_files_s3_bucket_region: region,
    aws_user_files_s3_bucket: bucket
} = config


const ProductItem = ({ product }) => {
    const { addProduct, cartItems, increase } = useContext(CartContext);
    const { isInWishlist, toggleWishlist } = useContext(WishlistContext);
    const [justAdded, setJustAdded] = useState(false);
    const url = `https://${bucket}.s3.${region}.amazonaws.com/public/${product.file.key}`

    const inCart = !!cartItems.find(item => item.id === product.id);
    const isFav = isInWishlist(product.id);

    const handleAddToCart = (e) => {
        if (e) e.preventDefault();
        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 1200);
        if (inCart) {
            increase(product);
        } else {
            addProduct(product);
        }
        notifyAdded();
    };

    const notifyAdded = () =>
        Notification({ title: 'success', message: 'Added to cart!', type: 'success' });

    const handleWishlist = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(product);
        Notification({
            title: 'success',
            message: isFav ? 'Removed from Wishlist' : 'Saved to Wishlist & Buy Again!',
            type: 'success'
        });
    };

    const stock = product.stockQuantity !== undefined && product.stockQuantity !== null ? product.stockQuantity : 50;
    const isOutOfStock = stock <= 0;
    const isLowStock = stock > 0 && stock <= 5;

    return (
        <Card>
            <WishlistBtn
                type="button"
                aria-label="Favorite product"
                $active={isFav}
                onClick={handleWishlist}
            >
                {isFav ? '❤️' : '🤍'}
            </WishlistBtn>

            <CardLink to={`/products/${product.id}`}>
                <ImgWrap>
                    <ResponsiveImage src={url} alt={product.description} objectFit="contain" />
                </ImgWrap>

                <CategoryTag>{getCategory(product)}</CategoryTag>
                <Name title={product.description}>{product.description}</Name>
            </CardLink>

            {isOutOfStock ? (
                <StockBadge $type="danger">🔴 Out of Stock</StockBadge>
            ) : isLowStock ? (
                <StockBadge $type="warning">⚠️ Only {stock} left!</StockBadge>
            ) : (
                <StockBadge $type="success">🟢 In Stock</StockBadge>
            )}

            <PriceRow>
                <Price>${convertCentsToDollar(product.price)}</Price>
                <Each>each</Each>
            </PriceRow>

            {isOutOfStock ? (
                <AddBtn disabled type="button" $disabled>
                    Out of Stock
                </AddBtn>
            ) : justAdded ? (
                <AddBtn type="button" style={{ background: '#0a8f57' }}>
                    ✓ Added!
                </AddBtn>
            ) : inCart ? (
                <AddBtn
                    $outline
                    type="button"
                    onClick={handleAddToCart}
                >
                    Add more +
                </AddBtn>
            ) : (
                <AddBtn
                    type="button"
                    onClick={handleAddToCart}
                >
                    Add to cart
                </AddBtn>
            )}
        </Card>
    );
}

export default ProductItem;

/* ---------- styles ---------- */
const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  container-type: inline-size;
  container-name: product-card;
  background: linear-gradient(145deg, #ffffff, #f9fafb);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: clamp(10px, 2vw, 16px);
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.03);
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(31, 45, 61, 0.08);
    border-color: rgba(1, 191, 113, 0.3);
  }

  @container product-card (max-width: 210px) {
    padding: 8px;
  }
`;

const WishlistBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  display: grid;
  place-items: center;
  font-size: 1.15rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.15);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const ImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 12px;
  background: #f1f5f9;
  margin-bottom: 8px;
  
  img {
    transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  ${Card}:hover & img {
    transform: scale(1.06);
  }
`;

const Name = styled.p`
  margin: 6px 2px 4px;
  color: #1a202c;
  font-size: clamp(0.9rem, 2vw, 1.05rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.4;
  text-transform: capitalize;
  word-break: break-word;
  transition: color 0.2s ease;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(2 * 1.4em);

  ${Card}:hover & {
    color: #0a8f57;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 2px 2px 12px;
`;

const Price = styled.span`
  color: #0a8f57;
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 800;
`;

const Each = styled.span`
  color: #718096;
  font-size: 0.75rem;
  font-weight: 600;
`;

const CategoryTag = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a5568;
  background: rgba(226, 232, 240, 0.6);
  padding: 4px 10px;
  border-radius: 6px;
  margin: 6px 2px 4px;
`;

const StockBadge = styled.span`
  align-self: flex-start;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  margin: 0 2px 8px;
  background: ${({ $type }) =>
    $type === 'danger' ? 'rgba(239, 68, 68, 0.12)'
    : $type === 'warning' ? 'rgba(245, 158, 11, 0.12)'
    : 'rgba(16, 185, 129, 0.12)'};
  color: ${({ $type }) =>
    $type === 'danger' ? '#dc2626'
    : $type === 'warning' ? '#d97706'
    : '#059669'};
`;

const AddBtn = styled.button`
  margin-top: auto;
  width: 100%;
  min-height: 44px;
  padding: clamp(10px, 2vw, 12px);
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow: ${({ $outline, $disabled }) => 
    $disabled || $outline ? 'none' : '0 4px 12px rgba(1, 191, 113, 0.2)'};

  background: ${({ $outline, $disabled }) => 
    $disabled ? '#e2e8f0' : $outline ? 'transparent' : 'linear-gradient(135deg, #01bf71, #0a8f57)'};
  
  color: ${({ $outline, $disabled }) => 
    $disabled ? '#94a3b8' : $outline ? '#0a8f57' : '#fff'};
  
  border: ${({ $outline, $disabled }) =>
    $disabled ? 'none' : $outline ? '1.5px solid rgba(1, 191, 113, 0.55)' : 'none'};

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-2px)')};
    box-shadow: ${({ $outline, $disabled }) => 
      $disabled ? 'none' 
      : $outline ? '0 4px 12px rgba(1, 191, 113, 0.1)' 
      : '0 6px 16px rgba(1, 191, 113, 0.3)'};
    background: ${({ $outline, $disabled }) => 
      $disabled ? '#e2e8f0' 
      : $outline ? 'rgba(1, 191, 113, 0.08)' 
      : 'linear-gradient(135deg, #02cc79, #0b9e60)'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  cursor: pointer;
  
  &:hover {
    text-decoration: none;
  }
`;
